import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Mic, Search, Star } from 'lucide-react'

import {
  MINIMAX_SYSTEM_VOICES,
  inferMiniMaxVoiceGender,
  miniMaxVoiceMeta,
  type MiniMaxVoice,
} from '@/features/studio/canvas/minimax-voices'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

type CanvasT = (key: string, opts?: Record<string, unknown>) => string

type VoiceTab = 'library' | 'mine' | 'favorites'

const FAV_STORAGE_KEY = 'silkreels.minimax.voiceFavorites'
const PAGE_SIZE_OPTIONS = [20, 50, 100]

function loadFavoriteIds(): Set<string> {
  try {
    const raw = localStorage.getItem(FAV_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((x) => typeof x === 'string'))
  } catch {
    return new Set()
  }
}

function saveFavoriteIds(ids: Set<string>) {
  localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify([...ids]))
}

export function MiniMaxVoiceLibraryDialog({
  open,
  onOpenChange,
  selectedVoiceId,
  onSelect,
  t,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedVoiceId: string
  onSelect: (voiceId: string) => void
  t: CanvasT
}) {
  const [tab, setTab] = useState<VoiceTab>('library')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavoriteIds())

  const voices = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list: MiniMaxVoice[] = MINIMAX_SYSTEM_VOICES
    if (tab === 'favorites') {
      list = list.filter((v) => favorites.has(v.id))
    }
    if (tab === 'mine') {
      list = []
    }
    if (!q) return list
    return list.filter(
      (v) =>
        v.id.toLowerCase().includes(q) ||
        v.name.toLowerCase().includes(q) ||
        v.language.toLowerCase().includes(q),
    )
  }, [favorites, query, tab])

  const totalPages = Math.max(1, Math.ceil(voices.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageVoices = voices.slice((safePage - 1) * pageSize, safePage * pageSize)

  const toggleFavorite = (voiceId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(voiceId)) next.delete(voiceId)
      else next.add(voiceId)
      saveFavoriteIds(next)
      return next
    })
  }

  const tabs: { id: VoiceTab; labelKey: string }[] = [
    { id: 'library', labelKey: 'canvas.minimaxVoiceLib.tabLibrary' },
    { id: 'mine', labelKey: 'canvas.minimaxVoiceLib.tabMine' },
    { id: 'favorites', labelKey: 'canvas.minimaxVoiceLib.tabFavorites' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="cn-mm-voice-lib nodrag nopan nowheel z-[10050] gap-0 overflow-hidden p-0 sm:max-w-3xl"
        onPointerDown={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b border-border/60 px-5 py-4">
          <DialogTitle>{t('canvas.minimaxVoiceLib.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-5 py-3">
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                className={clsx(
                  'cn-mm-voice-lib__tab',
                  tab === item.id && 'cn-mm-voice-lib__tab--active',
                )}
                onClick={() => {
                  setTab(item.id)
                  setPage(1)
                }}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>
          <div className="ml-auto flex min-w-[220px] flex-1 items-center gap-2 sm:max-w-xs">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setPage(1)
                }}
                placeholder={t('canvas.minimaxVoiceLib.searchPlaceholder')}
                className="cn-mm-voice-lib__search h-8 w-full rounded-lg border border-border bg-background/60 pr-2 pl-8 text-xs outline-none focus:border-primary/40"
              />
            </div>
          </div>
        </div>

        <div className="studio-scrollbar-hidden max-h-[min(52vh,520px)] overflow-y-auto px-3 py-2">
          {tab === 'mine' ? (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              {t('canvas.minimaxVoiceLib.mineEmpty')}
            </p>
          ) : pageVoices.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              {t('canvas.minimaxVoiceLib.empty')}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {pageVoices.map((voice) => {
                const selected = voice.id === selectedVoiceId
                const gender = inferMiniMaxVoiceGender(voice)
                return (
                  <li
                    key={voice.id}
                    className="cn-mm-voice-lib__row flex items-center gap-3 rounded-xl px-2 py-2"
                  >
                    <span className="cn-mm-voice-lib__icon inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground">
                      <Mic className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-foreground">{voice.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{miniMaxVoiceMeta(voice)}</div>
                    </div>
                    <span className="hidden text-[10px] text-muted-foreground sm:inline">
                      {gender === 'male'
                        ? t('canvas.minimaxVoiceLib.genderMale')
                        : gender === 'female'
                          ? t('canvas.minimaxVoiceLib.genderFemale')
                          : ''}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant={selected ? 'secondary' : 'default'}
                      disabled={selected}
                      className="h-8 min-w-[56px] px-3 text-xs"
                      onClick={() => {
                        onSelect(voice.id)
                        onOpenChange(false)
                      }}
                    >
                      {selected ? t('canvas.minimaxVoiceLib.selected') : t('canvas.minimaxVoiceLib.select')}
                    </Button>
                    <button
                      type="button"
                      className={clsx(
                        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted/60',
                        favorites.has(voice.id) && 'text-amber-400',
                      )}
                      aria-label={t('canvas.minimaxVoiceLib.favorite')}
                      onClick={() => toggleFavorite(voice.id)}
                    >
                      <Star className={clsx('h-4 w-4', favorites.has(voice.id) && 'fill-current')} />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {tab !== 'mine' && voices.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-5 py-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={clsx(
                    'cn-mm-voice-lib__page',
                    safePage === n && 'cn-mm-voice-lib__page--active',
                  )}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              {totalPages > 5 ? <span className="px-1">…</span> : null}
              {totalPages > 5 ? (
                <button
                  type="button"
                  className={clsx(
                    'cn-mm-voice-lib__page',
                    safePage === totalPages && 'cn-mm-voice-lib__page--active',
                  )}
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages}
                </button>
              ) : null}
            </div>
            <ProtoDropdown
              compact
              portal
              className="w-[96px]"
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v))
                setPage(1)
              }}
              options={PAGE_SIZE_OPTIONS.map((n) => ({
                value: String(n),
                label: t('canvas.minimaxVoiceLib.pageSize', { count: n }),
              }))}
            />
            <span className="ml-auto">{t('canvas.minimaxVoiceLib.total', { count: voices.length })}</span>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
